import React from "react";
import { useSearchParams } from "react-router-dom";
import { Upload, FileText, Building2, MapPin } from "lucide-react";
import { useUploadDocuments } from "./UploadDocumentsData";
import COMPANY_LOGO from "../../assets/logo/AcrossLogo.png";
import "./index.scss";

const UploadDocuments = () => {
    const [searchParams] = useSearchParams();

    const {
        uploadedFiles,
        errors,
        uploadingStatus,
        uploadProgress,
        requiredDocumentsField,
        isLoadingDocuments,
        handleFileSelect,
    } = useUploadDocuments(searchParams);

    // Calculate completion
    const totalDocuments = requiredDocumentsField.length;
    const completedDocuments = requiredDocumentsField.filter(doc => {
        const documentKey = (doc.entityDocumentId && doc.entityDocumentId !== 0)
            ? doc.entityDocumentId
            : doc.documentRequirementId;
        return uploadingStatus[documentKey] === "success";
    }).length;

    // Show loading state
    if (isLoadingDocuments) {
        return (
            <div className="upload-documents-container">
                <div className="upload-header">
                    <img src={COMPANY_LOGO} alt="Company Logo" className="company-logo" />
                </div>
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading required documents...</p>
                </div>
            </div>
        );
    }

    // Show error if fetch failed
    if (errors.fetch) {
        return (
            <div className="upload-documents-container">
                <div className="upload-header">
                    <img src={COMPANY_LOGO} alt="Company Logo" className="company-logo" />
                </div>
                <div className="error-state">
                    <p className="error-message">{errors.fetch}</p>
                    <button onClick={() => window.location.reload()} className="retry-button">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const getDocumentIcon = (documentName) => {
        if (documentName.toLowerCase().includes('bank')) return Building2;
        if (documentName.toLowerCase().includes('address')) return MapPin;
        return FileText;
    };

    return (
        <div className="upload-documents-container">
            {/* Header with Logo */}
            <div className="upload-header">
                <img src={COMPANY_LOGO} alt="Company Logo" className="company-logo" />
            </div>

            {/* Progress Section */}
            <div className="progress-section">
                <div className="progress-info">
                    <span className="progress-count">{completedDocuments}</span>
                    <span className="progress-separator">/</span>
                    <span className="progress-total">{totalDocuments} Completed</span>
                </div>
                <div className="progress-bar-container">
                    <div
                        className="progress-bar-fill"
                        style={{ width: `${(completedDocuments / totalDocuments) * 100}%` }}
                    />
                </div>
            </div>

            {/* Subtitle */}
            <p className="upload-subtitle">
                Please upload the required documents. Files will be uploaded automatically.
            </p>

            {/* Documents List */}
            <div className="documents-list">
                {requiredDocumentsField.map((document) => {
                    const documentKey = (document.entityDocumentId && document.entityDocumentId !== 0)
                        ? document.entityDocumentId
                        : document.documentRequirementId;
                    const status = uploadingStatus[documentKey];
                    const progress = uploadProgress[documentKey] || 0;
                    const Icon = getDocumentIcon(document.documentName);

                    return (
                        <div
                            key={documentKey}
                            className={`document-item ${status === "success" ? "completed" : ""}`}
                        >
                            {/* Left Side - Document Info */}
                            <div className="document-left">
                                <div className="document-icon-wrapper">
                                    <Icon size={24} className="document-icon" />
                                </div>
                                <div className="document-details">
                                    <div className="document-title-row">
                                        <h3 className="document-name">{document.documentName}</h3>
                                        {document.required && (
                                            <span className="required-badge">* Required</span>
                                        )}
                                    </div>
                                    <p className="document-description">
                                        {document.description || `Upload ${document.documentName.toLowerCase()}`}
                                    </p>
                                    <div className="document-meta">
                                        <span className="meta-item">
                                            📄 Supported: PDF, JPG, PNG
                                        </span>
                                        <span className="meta-separator">|</span>
                                        <span className="meta-item">Max: 5MB</span>
                                    </div>
                                    <div className="document-status-row">
                                        {status === "success" && (
                                            <span className="status-badge success">
                                                Status: Uploaded
                                            </span>
                                        )}
                                        {status === "uploading" && (
                                            <span className="status-badge uploading">
                                                Status: Uploading... {progress}%
                                            </span>
                                        )}
                                        {status === "error" && (
                                            <span className="status-badge error">
                                                Status: Upload Failed
                                            </span>
                                        )}
                                        {!status && (
                                            <span className="status-badge not-uploaded">
                                                Status: Not Uploaded
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right Side - Upload Icon Only */}
                            <div className="document-right">
                                <label
                                    htmlFor={`file-${documentKey}`}
                                    className={`upload-area ${status === "uploading" ? "disabled" : ""}`}
                                >
                                    <input
                                        type="file"
                                        id={`file-${documentKey}`}
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) => handleFileSelect(e, document)}
                                        disabled={status === "uploading"}
                                        className="file-input"
                                    />
                                    <div className="upload-content">
                                        <Upload size={28} className="upload-icon" />
                                        <p className="upload-text">
                                            Drag & Drop or Click to Upload
                                        </p>
                                    </div>
                                    <div className="upload-info">
                                        <span className="info-text">
                                            Supported: PDF, JPG, PNG | Max: 5MB
                                        </span>
                                    </div>
                                </label>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default UploadDocuments;
